import { Statuses } from '@geee-be/core';
import type { Organization } from '@local/common-lib';
import type { FC } from 'react';
import { useParams } from 'react-router-dom';
import { MiddleSpinner } from '../../components/middle-spinner/index.js';
import { useAxios } from '../../hooks/use-axios.js';
import { useSetTitle } from '../../hooks/use-title.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import type { ExistingEntityViewProps } from '../../utils/types.js';
import { ErrorPage } from '../error/index.js';
import { NotFoundPage } from '../not-found/index.js';

const ExistingOrganizationPageInner: FC<{
  id: string;
  isSa: boolean;
  view: FC<ExistingEntityViewProps<Organization.Type>>;
}> = ({ id, isSa, view: View }) => {
  const url = isSa
    ? `/api/v1/organization/${encodeURIComponent(id)}`
    : '/api/v1/my/organization';

  const [response, refresh, fetchNow] = useAxios<Organization.Type>(url);
  useSetTitle(
    response.loading
      ? 'Loading...'
      : response.error
        ? 'Error'
        : response.data.name,
  );

  if (response.loading) return <MiddleSpinner />;
  if (response.error?.response?.status === Statuses.NOT_FOUND) {
    return <NotFoundPage />;
  }
  if (response.error) return <ErrorPage error={response.error} />;

  return (
    <View data={response.data} fetchNow={fetchNow} id={id} refresh={refresh} />
  );
};

export const ExistingOrganizationPage: FC<{
  view: FC<ExistingEntityViewProps<Organization.Type>>;
}> = ({ view }) => {
  const auth = useAuthenticated();
  const isSa = auth.userInfo.isSuperAdmin;
  const params = useParams<{ id: string }>();
  if (!params.id && isSa) return <ErrorPage error={new Error('Invalid URL')} />;
  const id = params.id ?? 'my';

  return <ExistingOrganizationPageInner id={id} isSa={isSa} view={view} />;
};
